// Participant access portal — data access.
//
// A confirmed participant receives an email with an access link + a 6-digit
// code. The link lands on /training-access/:token; the participant verifies the
// code and gains a cookie-backed session to reach their course materials.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  AccessChallenge,
  EnrollmentResult,
  ParticipantAccessView,
  ParticipantAnnouncement,
  ParticipantConversation,
  ParticipantConversationSummary,
  RequestAccessLinkResult,
} from '@munifocus/contracts-web';
import { accessApi } from '../lib/api-client';

function currentLang(language: string | undefined): 'en' | 'fr' {
  return language?.startsWith('fr') ? 'fr' : 'en';
}

/** Status of the code challenge behind a link (does not send a new code). */
export function useAccessChallenge(token: string | undefined) {
  return useQuery({
    queryKey: ['access-challenge', token],
    enabled: Boolean(token),
    retry: false,
    queryFn: async () => {
      const { data } = await accessApi.get<AccessChallenge>(`/access/challenge/${token}`);
      return data;
    },
  });
}

/** The verified participant's sessions + materials; drives the portal. */
export function useParticipantMe(options: { enabled?: boolean } = {}) {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  return useQuery({
    queryKey: ['access-me', lang],
    enabled: options.enabled ?? true,
    retry: false,
    queryFn: async () => {
      const { data } = await accessApi.get<ParticipantAccessView>('/access/me', {
        params: { lang },
      });
      return data;
    },
  });
}

export function useVerifyAccess() {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { token: string; code: string }) => {
      const { data } = await accessApi.post<ParticipantAccessView>('/access/verify', {
        token: vars.token,
        code: vars.code,
        language: lang,
      });
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['access-me', lang], data);
    },
  });
}

export function useResendAccessCode() {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await accessApi.post<AccessChallenge>('/access/resend', {
        token,
        language: lang,
      });
      return data;
    },
  });
}

/**
 * "Email me a new access link": for a participant with no link to hand. The API
 * answers the same way whether or not the address has a registration, so the
 * page shows one neutral message on success.
 */
export function useRequestAccessLink() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { data } = await accessApi.post<RequestAccessLinkResult>('/access/request-link', {
        email,
      });
      return data;
    },
  });
}

/** Self-register the verified participant for another published session. */
export function useRegisterMore() {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      const { data } = await accessApi.post<EnrollmentResult>('/access/me/register', {
        slug,
        language: lang,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-me'] });
    },
  });
}

/**
 * Every query that holds this participant's data. Signing out must drop all of
 * them — the sessions, announcements, message threads, certificates and inbox —
 * not just the profile, or the next person at a shared computer is one Back
 * press from the previous participant's messages.
 */
function isParticipantQuery(key: readonly unknown[]): boolean {
  const head = key[0];
  return (
    typeof head === 'string' && (head.startsWith('access-') || head.startsWith('participant-'))
  );
}

export function useParticipantLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await accessApi.post('/access/logout');
    },
    onSuccess: () => {
      queryClient.removeQueries({ predicate: (query) => isParticipantQuery(query.queryKey) });
    },
  });
}

// --- Instructor communications ----------------------------------------------

/** Notices the instructor broadcast to the participant's sessions. */
export function useParticipantAnnouncements() {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  return useQuery({
    queryKey: ['access-announcements', lang],
    retry: false,
    queryFn: async () => {
      const { data } = await accessApi.get<ParticipantAnnouncement[]>('/access/announcements', {
        params: { lang },
      });
      return data;
    },
  });
}

/** One thread per session the participant is confirmed for (summaries only). */
export function useParticipantConversations() {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  return useQuery({
    queryKey: ['access-conversations', lang],
    retry: false,
    queryFn: async () => {
      const { data } = await accessApi.get<ParticipantConversationSummary[]>(
        '/access/conversations',
        { params: { lang } },
      );
      return data;
    },
  });
}

/** A single session's thread. Fetching it clears the participant's unread count,
 *  so it is only enabled once the participant actually opens the thread. */
export function useParticipantConversation(sessionId: string, options: { enabled?: boolean } = {}) {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  return useQuery({
    queryKey: ['access-conversation', sessionId, lang],
    enabled: options.enabled ?? true,
    retry: false,
    queryFn: async () => {
      const { data } = await accessApi.get<ParticipantConversation>(
        `/access/conversations/${sessionId}`,
        { params: { lang } },
      );
      return data;
    },
  });
}

export function useSendParticipantMessage(sessionId: string) {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const { data } = await accessApi.post<ParticipantConversation>(
        `/access/conversations/${sessionId}/messages`,
        { body },
        { params: { lang } },
      );
      return data;
    },
    onSuccess: (data) => {
      // The POST returns the refreshed thread, so seed it rather than refetch.
      queryClient.setQueryData(['access-conversation', sessionId, lang], data);
      void queryClient.invalidateQueries({ queryKey: ['access-conversations'] });
    },
  });
}
